import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  TOpsProgramDetailEx,
  TOpsProgramContact,
  TOpsProgramFields,
  updateOpsProgramFields,
} from '../../../requests/opsRegistrationsExtra';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  Pill,
} from '../ui';
import ContactPicker from './ContactPicker';
import {
  bodyFor,
  Draft,
  draftFor,
  FieldSpec,
  GAP_LABELS,
  PanelId,
  PANELS,
  stripHtml,
} from './shared';

/**
 * The handoff fields — Program Information, Strategy, Portal message — each
 * saved on its own. One panel is edited at a time.
 */
const FieldPanels: React.FC<{
  programId: string;
  program: TOpsProgramDetailEx;
}> = ({ programId, program }) => {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<PanelId | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [draftContacts, setDraftContacts] = useState<
    Record<string, TOpsProgramContact | null>
  >({});

  const refreshDetail = () =>
    queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });

  const savePanel = useMutation({
    mutationFn: () => updateOpsProgramFields(programId, bodyFor(panel!, draft)),
    onSuccess: () => {
      showToast(`${PANELS[panel!].title} saved`, 'success');
      setPanel(null);
      refreshDetail();
      queryClient.invalidateQueries({ queryKey: ['opsPrograms'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const openPanel = (id: PanelId) => {
    if (!program) return;
    setDraft(draftFor(id, program.fields));
    setDraftContacts({
      contractPocId: program.contractPoc,
      secondaryContactId: program.secondaryContact,
    });
    setPanel(id);
  };

  const renderValue = (spec: FieldSpec, f: TOpsProgramFields) => {
    const v = f[spec.key];
    if (spec.kind === 'bool')
      return v ? (
        <Pill value="Yes" tone="good" />
      ) : (
        <span className="text-sm text-gray-bold">No</span>
      );
    if (spec.kind === 'contact') {
      const c =
        spec.key === 'contractPocId'
          ? program?.contractPoc
          : program?.secondaryContact;
      return c ? (
        <div className="text-sm text-gray-five">
          <span className="font-medium text-gray-four">{c.name}</span>
          {(c.title || c.email) && (
            <span className="ml-2 text-xs text-gray-bold">
              {[c.title, c.email].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ) : (
        <span className="text-sm text-gray-bold">—</span>
      );
    }
    if (v == null || v === '')
      return <span className="text-sm text-gray-bold">—</span>;
    if (spec.kind === 'url')
      return (
        <a
          className="break-all text-sm text-blue-primary hover:underline"
          href={String(v)}
          target="_blank"
          rel="noreferrer"
        >
          {String(v)}
        </a>
      );
    if (spec.kind === 'multi')
      return (
        <div className="text-sm text-gray-five">
          {String(v).split(';').join(' · ')}
        </div>
      );
    return (
      <div className="whitespace-pre-wrap text-sm text-gray-five">
        {spec.kind === 'textarea' ? stripHtml(String(v)) : String(v)}
        {spec.suffix ?? ''}
      </div>
    );
  };

  const renderInput = (spec: FieldSpec) => {
    const v = draft[spec.key];
    const setV = (val: string | boolean) =>
      setDraft((d) => ({ ...d, [spec.key]: val }));
    switch (spec.kind) {
      case 'bool':
        return (
          <label className="flex items-center gap-2 text-sm text-gray-five">
            <input
              type="checkbox"
              className="ops-check h-4 w-4 accent-blue-primary"
              checked={Boolean(v)}
              onChange={(e) => setV(e.target.checked)}
            />
            {spec.hint ?? 'Yes'}
          </label>
        );
      case 'select':
        return (
          <select
            className={`${inputClass} w-full`}
            value={String(v ?? '')}
            onChange={(e) => setV(e.target.value)}
          >
            <option value="">—</option>
            {spec.options?.map((o) => <option key={o}>{o}</option>)}
            {v && !spec.options?.includes(String(v)) && (
              <option value={String(v)}>{String(v)}</option>
            )}
          </select>
        );
      case 'multi': {
        const chosen = String(v ?? '')
          .split(';')
          .filter(Boolean);
        return (
          <div className="flex flex-wrap gap-3">
            {spec.options?.map((o) => (
              <label
                key={o}
                className="flex items-center gap-1.5 text-sm text-gray-five"
              >
                <input
                  type="checkbox"
                  className="ops-check h-4 w-4 accent-blue-primary"
                  checked={chosen.includes(o)}
                  onChange={(e) =>
                    setV(
                      (e.target.checked
                        ? [...chosen, o]
                        : chosen.filter((x) => x !== o)
                      ).join(';'),
                    )
                  }
                />
                {o}
              </label>
            ))}
          </div>
        );
      }
      case 'number':
        return (
          <input
            type="number"
            min="0"
            step={spec.suffix === '%' ? '1' : '1'}
            className={`${inputClass} w-full`}
            value={String(v ?? '')}
            onChange={(e) => setV(e.target.value)}
          />
        );
      case 'textarea':
        return (
          <textarea
            className={`${inputClass} w-full`}
            rows={4}
            value={String(v ?? '')}
            placeholder={spec.hint}
            onChange={(e) => setV(e.target.value)}
          />
        );
      case 'contact':
        return (
          <ContactPicker
            programId={programId}
            value={String(v ?? '')}
            current={draftContacts[spec.key] ?? null}
            onChange={(id, c) => {
              setV(id);
              setDraftContacts((x) => ({ ...x, [spec.key]: c }));
            }}
          />
        );
      case 'readonly':
        return null;
      default:
        return (
          <input
            type={spec.kind === 'url' ? 'url' : 'text'}
            className={`${inputClass} w-full`}
            value={String(v ?? '')}
            placeholder={spec.hint}
            onChange={(e) => setV(e.target.value)}
          />
        );
    }
  };

  const renderPanel = (id: PanelId) => {
    const spec = PANELS[id];
    const editing = panel === id;
    const gaps = program?.gaps;
    return (
      <Card
        key={id}
        title={spec.title}
        right={
          !editing ? (
            <span className="flex items-center gap-2">
              {id === 'info' && gaps && (
                <Pill
                  value={`${gaps.filled} of ${gaps.total} filled`}
                  tone={gaps.missing.length ? 'warn' : 'good'}
                />
              )}
              <button
                className={btnSecondary}
                disabled={panel != null}
                onClick={() => openPanel(id)}
              >
                Edit
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <button
                className={btnPrimary}
                onClick={() => savePanel.mutate()}
                disabled={savePanel.isPending}
              >
                {savePanel.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className={btnGhost} onClick={() => setPanel(null)}>
                Cancel
              </button>
            </span>
          )
        }
      >
        <div className="px-5 py-4">
          {spec.note && (
            <div className="mb-3 text-xs text-gray-bold">{spec.note}</div>
          )}
          {id === 'info' && gaps && gaps.missing.length > 0 && !editing && (
            <div className="mb-3 rounded-md bg-yellow-500/40 px-3 py-2 text-xs text-yellow-900">
              Still to fill:{' '}
              {gaps.missing.map((k) => GAP_LABELS[k] ?? k).join(' · ')}
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
            {spec.fields.map((f) => (
              <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
                <div className={labelClass}>
                  {f.label}
                  {f.kind === 'readonly' && (
                    <span className="ml-1 font-normal normal-case text-gray-two">
                      · from the opportunity
                    </span>
                  )}
                </div>
                {editing && f.kind !== 'readonly'
                  ? renderInput(f)
                  : program && renderValue(f, program.fields)}
                {editing && f.hint && f.kind !== 'bool' && (
                  <div className="mt-0.5 text-xs text-gray-two">{f.hint}</div>
                )}
              </div>
            ))}
            {id === 'strategy' &&
              program?.fields.programSuccessCriteria &&
              !editing && (
                <div className="md:col-span-2">
                  <div className={labelClass}>
                    Program success criteria
                    <span className="ml-1 font-normal normal-case text-gray-two">
                      · older field, read-only
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-gray-five">
                    {program.fields.programSuccessCriteria}
                  </div>
                </div>
              )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="mb-4 grid gap-4 lg:grid-cols-2">
      {renderPanel('info')}
      <div className="grid gap-4 content-start">
        {renderPanel('strategy')}
        {renderPanel('portal')}
      </div>
    </div>
  );
};

export default FieldPanels;
